import React, { useState, useEffect } from 'react';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import SearchableSelect from '../../../../components/SearchableSelect';
import Icon from '../../../../components/Icon';
import { FiEdit2 } from 'react-icons/fi';

type Branch = {
    id: string;
    bankId: string;
    branchCode: string;
    address: string;
    mobile: string;
    locationId: string;
    securityCode: string;
    locationName: string;
    psoId?: string;
    psoName: string;
    psoEmail: string;
    psoContact: string;
    name?: string;
    branchManagerId?: string;
    operationalManagerId?: string;
    areaManagerId?: string;
};

type Manager = {
    id: string;
    name: string;
    email: string;
    mobile: string;
    type?: string;
};

type PSO = {
    id: string;
    name: string;
    email?: string;
    contact?: string;
};

type BranchDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    branch: Branch | null;
    managers: Record<string, Manager>;
    bankId?: string;
    onUpdate?: () => void;
    editMode?: boolean;
};

export default function BranchDetailsModal({ isOpen, onClose, branch, managers, bankId, onUpdate, editMode = false }: BranchDetailsModalProps) {
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const isSuperAdmin = isBankUser; // Bank users are super admins
    const [isEditing, setIsEditing] = useState(editMode);
    const [loading, setLoading] = useState(false);
    const [editedBranch, setEditedBranch] = useState<any>(null);
    const [customLocationName, setCustomLocationName] = useState('');
    const [editedManagers, setEditedManagers] = useState<Record<string, Partial<Manager>>>({});

    // Reset to view mode when modal closes, or set initial edit mode when opening
    useEffect(() => {
        if (!isOpen) {
            setIsEditing(false);
        } else if (editMode && isBankUser) {
            setIsEditing(true);
        }
    }, [isOpen, editMode, isBankUser]);

    // Simplified state management
    const [locations, setLocations] = useState<Array<{ id: string; name: string }>>([]);
    const [allManagers, setAllManagers] = useState<Manager[]>([]);
    const [psos, setPsos] = useState<PSO[]>([]);

    useEffect(() => {
        if (branch) {
            setEditedBranch({
                ...branch,
                mobile: branch.mobile || '',
                address: branch.address || '',
                locationId: branch.locationId || '',
                psoId: branch.psoId || '',
                branchCode: branch.branchCode || '',
                securityCode: branch.securityCode || '',
                name: branch.name || '',
            });
            setCustomLocationName('');
            setEditedManagers({});
        }
    }, [branch, isOpen]);

    useEffect(() => {
        if (isOpen && bankId) {
            const loadData = async () => {
                try {
                    const locSnap = await getDocs(collection(db, 'locations'));
                    const locList = locSnap.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name: string }));
                    setLocations(locList);

                    const psoSnap = await getDocs(collection(db, 'banks', bankId, 'pos'));
                    const psoList = psoSnap.docs.map(d => ({
                        id: d.id,
                        ...d.data()
                    } as PSO));
                    setPsos(psoList);

                    const mgrSnap = await getDocs(collection(db, 'banks', bankId, 'managers'));
                    const managerList = mgrSnap.docs.map(d => ({
                        id: d.id,
                        ...d.data()
                    } as Manager));
                    setAllManagers(managerList);
                } catch (err) {
                    console.error('Error loading data:', err);
                }
            };
            loadData();
        }
    }, [isOpen, bankId]);

    useEffect(() => {
        if (branch && allManagers.length > 0) {
            const updatedManagers: Record<string, Partial<Manager>> = { ...editedManagers };
            ['branchManagerId', 'operationalManagerId', 'areaManagerId'].forEach(key => {
                const mgrId = branch[key as keyof Branch] as string;
                if (mgrId && !editedManagers[key]) {
                    const foundMgr = allManagers.find(m => m.id === mgrId);
                    if (foundMgr) {
                        updatedManagers[key] = { ...foundMgr };
                    }
                }
            });
            setEditedManagers(updatedManagers);
        }
    }, [allManagers, branch, isOpen]);

    if (!branch) return null;

    const handleFieldChange = (field: string, value: string) => {
        setEditedBranch((prev: any) => prev ? { ...prev, [field]: value } : null);
    };

    const handleSave = async () => {
        if (!editedBranch || !bankId || !branch) return;

        setLoading(true);
        try {
            const branchRef = doc(db, 'banks', bankId, 'branch', branch.id);
            const updateData: any = {
                mobile: editedBranch.mobile || '',
                address: editedBranch.address || '',
            };

            // Super admin (bank users) can update all fields including branch code and client code
            if (isSuperAdmin) {
                if (editedBranch.branchCode !== undefined) {
                    updateData.branchCode = editedBranch.branchCode;
                }
                if (editedBranch.securityCode !== undefined) {
                    updateData.securityCode = editedBranch.securityCode;
                }
                if (editedBranch.name !== undefined) {
                    updateData.name = editedBranch.name;
                }
            }

            if (editedBranch.psoId !== undefined) {
                updateData.psoId = editedBranch.psoId || null;
            }

            // Handle custom location
            if (editedBranch.locationId === 'custom' && customLocationName) {
                const { addDoc, collection } = await import('firebase/firestore');
                const locRef = await addDoc(collection(db, 'locations'), {
                    name: customLocationName.trim()
                });
                updateData.locationId = locRef.id;
            } else if (editedBranch.locationId) {
                updateData.locationId = editedBranch.locationId;
            }

            // Update manager IDs
            updateData.branchManagerId = editedBranch.branchManagerId || null;
            updateData.operationalManagerId = editedBranch.operationalManagerId || null;
            updateData.areaManagerId = editedBranch.areaManagerId || null;

            await updateDoc(branchRef, updateData);

            // Update manager details if edited
            for (const [key, managerData] of Object.entries(editedManagers)) {
                const managerId = editedBranch[key as 'branchManagerId' | 'operationalManagerId' | 'areaManagerId'];
                if (managerId && managerData && managerData.name) {
                    const managerRef = doc(db, 'banks', bankId, 'managers', managerId);
                    const managerUpdateData: any = {};
                    if (managerData.name) managerUpdateData.name = managerData.name;
                    if (managerData.email !== undefined) managerUpdateData.email = managerData.email;
                    if (managerData.mobile !== undefined) managerUpdateData.mobile = managerData.mobile;

                    if (Object.keys(managerUpdateData).length > 0) {
                        await updateDoc(managerRef, managerUpdateData);
                    }
                }
            }

            setIsEditing(false);
            if (onUpdate) onUpdate();
            onClose();
        } catch (err: any) {
            console.error('Error updating branch:', err);
            alert('Failed to update branch: ' + (err.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        // Reset to original values
        if (branch) {
            setEditedBranch({
                ...branch,
                mobile: branch.mobile || '',
                address: branch.address || '',
                locationId: branch.locationId || '',
                psoId: branch.psoId || '',
                branchCode: branch.branchCode || '',
                securityCode: branch.securityCode || '',
                name: branch.name || '',
            });
            const initialManagers: Record<string, Partial<Manager>> = {};
            if (branch.branchManagerId && managers[branch.branchManagerId]) {
                initialManagers.branchManagerId = { ...managers[branch.branchManagerId] };
            }
            if (branch.operationalManagerId && managers[branch.operationalManagerId]) {
                initialManagers.operationalManagerId = { ...managers[branch.operationalManagerId] };
            }
            if (branch.areaManagerId && managers[branch.areaManagerId]) {
                initialManagers.areaManagerId = { ...managers[branch.areaManagerId] };
            }
            setEditedManagers(initialManagers);
        }
        setIsEditing(false);
    };

    const handleClose = () => {
        // If editing, cancel first to reset changes
        if (isEditing) {
            handleCancel();
        }
        onClose();
    };

    const managerFields = [
        { key: 'branchManagerId', label: 'Branch Manager' },
        { key: 'operationalManagerId', label: 'Operational Manager' },
        { key: 'areaManagerId', label: 'Area Manager' },
    ] as const;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Branch Details"
            size="lg"
        >
            <div className="p-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Edit Button in Header Area - Only show for super admin (bank users) */}
                {isSuperAdmin && !isEditing && (
                    <div className="mb-4 flex justify-end">
                        <button
                            onClick={() => setIsEditing(true)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                            aria-label="Edit branch details"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                    </div>
                )}
                <div className="space-y-4">
                    {/* Basic Information */}
                    <div className="space-y-4">
                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Bank Name
                            </label>
                            <input
                                type="text"
                                readOnly={!isEditing || !isSuperAdmin}
                                value={editedBranch?.name ?? branch?.name ?? 'N/A'}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                                className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${isEditing && isSuperAdmin
                                    ? 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    : 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    }`}
                            />
                        </div>

                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Branch Code
                            </label>
                            <input
                                type="text"
                                readOnly={!isEditing || !isSuperAdmin}
                                value={editedBranch?.branchCode ?? branch?.branchCode ?? ''}
                                onChange={(e) => handleFieldChange('branchCode', e.target.value)}
                                className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${isEditing && isSuperAdmin
                                    ? 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    : 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    }`}
                            />
                        </div>

                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Client Code
                            </label>
                            <input
                                type="text"
                                readOnly={!isEditing || !isSuperAdmin}
                                value={editedBranch?.securityCode ?? branch?.securityCode ?? ''}
                                onChange={(e) => handleFieldChange('securityCode', e.target.value)}
                                className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${isEditing && isSuperAdmin
                                    ? 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    : 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    }`}
                            />
                        </div>

                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Mobile
                            </label>
                            <input
                                type="text"
                                readOnly={!isEditing}
                                value={editedBranch?.mobile ?? branch?.mobile ?? ''}
                                onChange={(e) => handleFieldChange('mobile', e.target.value)}
                                className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${isEditing
                                    ? 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    : 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    }`}
                            />
                        </div>

                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Address
                            </label>
                            <textarea
                                readOnly={!isEditing}
                                value={editedBranch?.address ?? branch?.address ?? ''}
                                onChange={(e) => handleFieldChange('address', e.target.value)}
                                rows={3}
                                className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${isEditing
                                    ? 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    : 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                    }`}
                            />
                        </div>

                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Location
                            </label>
                            {isEditing ? (
                                <select
                                    value={editedBranch?.locationId ?? branch?.locationId ?? ''}
                                    onChange={(e) => handleFieldChange('locationId', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                >
                                    <option value="">Select Location</option>
                                    {locations.map(loc => (
                                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                                    ))}
                                    <option value="custom">+ Other (Enter Custom)</option>
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    readOnly
                                    value={locations.find(l => l.id === branch.locationId)?.name || branch?.locationName || 'N/A'}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                />
                            )}
                        </div>

                        {isEditing && editedBranch?.locationId === 'custom' && (
                            <div>
                                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Custom Location Name
                                </label>
                                <input
                                    type="text"
                                    value={customLocationName}
                                    onChange={(e) => setCustomLocationName(e.target.value)}
                                    placeholder="Enter custom location name"
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                />
                            </div>
                        )}

                        <div>
                            <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                PSO
                            </label>
                            {isEditing ? (
                                <select
                                    value={editedBranch?.psoId || ''}
                                    onChange={(e) => handleFieldChange('psoId', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                >
                                    <option value="">Select PSO</option>
                                    {psos.map(pso => (
                                        <option key={pso.id} value={(pso as any).psoId || pso.id}>
                                            {pso.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    readOnly
                                    value={branch?.psoName || 'N/A'}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                />
                            )}
                        </div>
                    </div>

                    {/* Manager Information */}
                    {managerFields.map(({ key, label }) => {
                        const currentMgrId = editedBranch?.[key] ?? branch?.[key] ?? '';
                        // Look for manager in both managers prop and allManagers state
                        const currentMgr = currentMgrId
                            ? (managers[currentMgrId] || allManagers.find(m => m.id === currentMgrId) || null)
                            : null;
                        const editedMgr = editedManagers[key] || currentMgr;

                        console.log(`Manager ${label}:`, {
                            currentMgrId,
                            foundInManagers: !!managers[currentMgrId],
                            foundInAllManagers: !!allManagers.find(m => m.id === currentMgrId),
                            currentMgr,
                            editedMgr
                        });

                        return (
                            <div
                                key={key}
                                className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
                            >
                                <h6 className="mb-4 text-base font-bold text-gray-900 dark:text-gray-100">
                                    {label}
                                </h6>
                                <div className="space-y-4">
                                    {isEditing ? (
                                        <SearchableSelect
                                            options={allManagers.map(mgr => ({
                                                id: mgr.id,
                                                label: mgr.name,
                                                value: mgr.id,
                                                details: {
                                                    email: mgr.email,
                                                    mobile: mgr.mobile,
                                                },
                                            }))}
                                            value={currentMgrId || ''}
                                            onChange={(value) => {
                                                handleFieldChange(key, value);
                                                const selectedMgr = allManagers.find(m => m.id === value);
                                                if (selectedMgr) {
                                                    setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...selectedMgr }
                                                    }));
                                                } else {
                                                    setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: {}
                                                    }));
                                                }
                                            }}
                                            placeholder={`Select ${label} (Optional)`}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            readOnly
                                            value={currentMgr ? currentMgr.name : 'No manager assigned'}
                                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                        />
                                    )}
                                    {currentMgr && (
                                        <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                            <div>
                                                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Name
                                                </label>
                                                <input
                                                    type="text"
                                                    readOnly={!isEditing}
                                                    value={editedMgr?.name || currentMgr.name || ''}
                                                    onChange={(e) => setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...prev[key], name: e.target.value }
                                                    }))}
                                                    className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${isEditing
                                                        ? 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                                        : 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                                        }`}
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Email
                                                </label>
                                                <input
                                                    type="email"
                                                    readOnly={!isEditing}
                                                    value={editedMgr?.email || currentMgr.email || ''}
                                                    onChange={(e) => setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...prev[key], email: e.target.value }
                                                    }))}
                                                    className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${isEditing
                                                        ? 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                                        : 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                                        }`}
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Mobile
                                                </label>
                                                <input
                                                    type="text"
                                                    readOnly={!isEditing}
                                                    value={editedMgr?.mobile || currentMgr.mobile || ''}
                                                    onChange={(e) => setEditedManagers(prev => ({
                                                        ...prev,
                                                        [key]: { ...prev[key], mobile: e.target.value }
                                                    }))}
                                                    className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${isEditing
                                                        ? 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                                        : 'border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                                        }`}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    {isEditing ? (
                        <>
                            <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
}
